import { Section, P, Callout, Table } from "./Primitives";
import { eur, OFFER, cashflow, paybackMonth, partnerCost, breakEvenSales } from "../../data/model";

const MEDIA_Y1 = 27900;
const BRAND = 15400;
const SALES = { y1: 60000, y24: 150000 };

const facts = (opt) => {
  const o = OFFER[opt];
  const flow = cashflow(60000, 90000, opt);
  const pt1 = partnerCost(opt, 12, SALES.y1);
  return {
    o,
    first: o.setup * 0.4,
    tranche: o.setup * 0.3,
    pt1,
    pt2: partnerCost(opt, 24, SALES.y24),
    y1: pt1 + MEDIA_Y1 + BRAND,
    pay: paybackMonth(flow),
    net24: flow[23],
  };
};

const Card = ({ opt, title, simple, f, testId, chosen }) => (
  <div className={`recap-card avoid-break ${chosen ? "recap-card--chosen" : ""}`} data-testid={testId}>
    {chosen && <div className="recap-badge">La scelta indicata dal cliente</div>}
    <div className="kicker">Opzione {opt}</div>
    <h3 className="font-display text-3xl text-navy mt-1">{title}</h3>
    <p className="text-sm italic text-[var(--ink-soft)] mt-2 mb-6 leading-relaxed">{simple}</p>
    <dl className="recap-list">
      <dt>Alla firma pagate</dt><dd data-testid={`${testId}-first`}>{eur(f.first)}</dd>
      <dt>Poi, in 2 rate (collaudo e lancio)</dt><dd>{eur(f.tranche)} + {eur(f.tranche)}</dd>
      <dt>Ogni mese, dal 3° mese</dt><dd>{eur(f.o.fee)}</dd>
      <dt>Sulle vendite alle aziende</dt><dd>{f.o.comm ? `${f.o.comm * 100}% (€ 10 ogni € 100)` : "Niente"}</dd>
      <dt>A PartnerTech nel 1° anno</dt><dd>{eur(f.pt1)}</dd>
      <dt>Spesa totale 1° anno*</dt><dd data-testid={`${testId}-y1`}><strong>{eur(f.y1)}</strong></dd>
      <dt>A PartnerTech in 2 anni</dt><dd>{eur(f.pt2)}</dd>
      <dt>Rientro dell'investimento</dt><dd data-testid={`${testId}-pay`}><strong>{f.pay}° mese</strong></dd>
      <dt>Guadagno netto dopo 2 anni</dt><dd data-testid={`${testId}-net`}><strong>{eur(f.net24)}</strong></dd>
    </dl>
  </div>
);

export const Recap = () => {
  const a = facts("A");
  const b = facts("B");
  return (
    <Section id="resoconto" num="09" kicker="Resoconto finale" title="Opzione A e Opzione B in parole semplici">
      <P>
        Due modi per arrivare allo stesso risultato. Cambia <strong>quando</strong> si paga e <strong>a chi va</strong> una parte del guadagno.
        I numeri seguono lo scenario medio: € 60.000 di vendite alle aziende il primo anno e € 150.000 in due anni.
      </P>
      <div className="grid md:grid-cols-2 gap-6 my-8">
        <Card testId="recap-b" opt="B" chosen title="Partner a performance" f={b}
          simple="È come aprire un'attività con un socio: si entra spendendo poco, e il socio guadagna solo se si vende. Su ogni € 100 venduti alle aziende tramite il portale, € 10 vanno a PartnerTech." />
        <Card testId="recap-a" opt="A" title="Chiavi in mano" f={a}
          simple="È come comprare il negozio invece di affittarlo: si paga di più all'inizio, ma poi tutto quello che si vende resta a Bella Napoli, senza percentuali." />
      </div>
      <Table
        testId="table-recap"
        head={["La domanda", "Opzione B", "Opzione A"]}
        rows={[
          ["Quanto serve per partire?", eur(b.first), eur(a.first)],
          ["Quanto si paga a PartnerTech ogni mese?", eur(b.o.fee), eur(a.o.fee)],
          ["Si paga una percentuale sulle vendite?", "Sì, 10% sulle vendite B2B del portale", "No"],
          ["Quanto si spende in tutto il 1° anno?*", eur(b.y1), eur(a.y1)],
          ["Quando si rientra dell'investimento?", `${b.pay}° mese`, `${a.pay}° mese`],
          ["Quanto si guadagna in 2 anni, al netto di tutto?", eur(b.net24), eur(a.net24)],
          ["Chi rischia se il portale vende poco?", "Il rischio è condiviso con PartnerTech", "Il rischio è tutto di Bella Napoli"],
        ]}
        foot="* Spesa totale = PartnerTech + pubblicità (€ 27.900 nel 1° anno) + restyling dei negozi (€ 15.400). La pubblicità si paga a Meta, Google e LinkedIn; il restyling ai fornitori."
      />
      <Callout title="Quale conviene?">
        <strong>L'Opzione B è la scelta giusta per partire</strong>: chiede solo {eur(b.first)} alla firma e si ripaga prima.
        L'Opzione A diventa più conveniente solo se le vendite alle aziende tramite il portale superano circa <strong>{eur(breakEvenSales(24))} in due anni</strong>.
        A quel punto la percentuale pagata con la B supera il risparmio iniziale.
        In entrambi i casi resta la clausola franchising: il 3% sulla fee d'ingresso di ogni nuovo affiliato.
      </Callout>
    </Section>
  );
};
